import { APPROVAL_QUERY_PARAM, AWAITING_APPROVAL_QUERY_VALUE } from './utils/bookingApproval';
import { DayFilter, dayFilterQuery } from './utils/bookingDay';

export const paths = {
    signup: '/signup',
    verifyEmail: '/verify-email',
    onboarding: '/onboarding',
    account: '/account',
    // The onboarding import (docs/plans/onboarding-import.md): one file fills the account.
    onboardingImport: '/account/onboarding-import',
    // Help center (#2685) — articles from docs/help-center/, route = /help/<section>/<file>.
    help: '/help',
    helpArticle: '/help/:section/:article',
    dashboard: '/dashboard',
    assets: '/assets',
    createAsset: '/assets/create',
    assetDetails: '/assets/:assetId',
    // Customers (H-3b) — our tenants' customers. The surface carries two tabs:
    // customer organizations (Verein / company / public body) and individual
    // customers (end-user accounts, H-3a).
    customers: '/customers',
    customerOrganizationCreate: '/customers/organizations/create',
    // "Organisationen importieren" (docs/plans/organization-import.md §5.3): customers:write.
    customerOrganizationImport: '/customers/organizations/import',
    customerOrganizationDetail: '/customers/organizations/:organizationId',
    customerAccountDetail: '/customers/accounts/:accountId',
    //Lockers
    depots: '/depots',
    depotDetails: '/depots/:depotId',
    // Object & station creation (asset side). The wire is released — the
    // target write endpoints (#2134) exist under /admin/inventory — so it is
    // reachable, from the create menu on the Objects page rather than from a
    // sidebar entry of its own: objects are managed there, and a station is how
    // a set of them gets created (PO, 2026-08-28).
    createStation: '/stations/create',
    // A house is the station wizard's "holds rooms, no doors" variant (plan
    // room-houses-and-combining-rooms §4 row 1) — same page, two steps.
    createHouse: '/stations/create-house',
    stationDetails: '/stations/:stationId',
    // Product detail (asset side — the fleet and its objects). Permission-gated
    // and reached from an object's "Product" link or the fleet rather than the
    // sidebar: the route is per-product, so there is no single entry to list.
    productDetails: '/products/:productId',
    locations: '/locations',
    locationDetails: '/locations/:locationId',
    cabinet: '/cabinet',
    cabinetDetails: '/cabinet/:locationId',
    addVisitorBooking: '/visitor-bookings/add',
    bookings: '/bookings',
    createRoomBooking: '/room-bookings/create',
    createVenueBooking: '/venue-bookings/create',
    // The venue create screen, booking a sports hall (PO, 2026-10-18).
    createHallBooking: '/hall-bookings/create',
    createRentalBooking: '/rental-bookings/create',
    createStorageBooking: '/storage-bookings/create',
    createSupBooking: '/sup-bookings/create',
    createKeysBooking: '/keys-bookings/create',
    createParcelBooking: '/parcel-bookings/create',
    createSeriesBooking: '/series-bookings/create',
    seriesBookingDetails: '/series-bookings/:seriesId',
    bookingDetails: '/bookings/:bookingId',
    bookingSearch: '/bookings/search',
    bookingDetailsSearch: '/bookings/search/:bookingId',
    issueReports: '/issue-reports',
    addIssueReports: '/issue-reports/add',
    addIssueReportForBooking: '/bookings/:bookingId/issue-reports/add',
    issueReportDetails: '/issue-reports/:issueReportId',
    statistics: '/statistics',
    vouchersCodes: '/vouchers-codes',
    // The tenant's issued invoices (#3016), over GET /admin/invoice.
    invoices: '/invoices',
    rentalProducts: '/rental-products',
    deposits: '/deposits',
    visitorBookings: '/visitor-bookings',
    unauthorized: '/unauthorized',

    //Rentals
    rentalBooking: '/rental-bookings',
    rentalBookingDetails: '/rental-bookings/:bookingId',
    roomBookingEdit: '/room-bookings/:bookingId/edit',
    rentalIssueReports: '/rental-issue-reports',
    rentalAddIssueReports: '/rental/add/issue-reports',
    rentalViewIssueReports: '/rental/view/issue-reports/:issueReportId',
    rentalApartment: '/rental-apartment',
    rentalAddOnServices: '/rental-add-on-services',
    rentalPolices: '/rental-polices',
    editVenueBooking: '/venue-bookings/:bookingId/edit',
    createBlocker: '/blockers/create',

    // Master Mode — cross-tenant operational views, gated by the
    // `master` role. Each page fans out per-tenant queries.
    masterBookings: '/master/bookings',
    masterProblemReports: '/master/problem-reports',
    masterLockers: '/master/lockers',
    // Tenant management (plan tenant-management-first-step): one request, not a fan-out.
    masterTenants: '/master/tenants',
};

export const makeDepotDetailsUrl = (depotId: string) =>
    paths.depotDetails.replace(':depotId', depotId);

export const makeLocationDetailsUrl = (locationId: string) =>
    paths.locationDetails.replace(':locationId', locationId);

export const makeBookingDetailsUrl = (bookingId: string) =>
    paths.bookingDetails.replace(':bookingId', bookingId);

export const makeBookingSearchDetailsUrl = (bookingId: string) =>
    paths.bookingDetailsSearch.replace(':bookingId', bookingId);

export const makeRentalBookingDetailsUrl = (bookingId: string) =>
    paths.rentalBookingDetails.replace(':bookingId', bookingId);

export const makeRoomBookingEditsUrl = (bookingId: string) =>
    paths.roomBookingEdit.replace(':bookingId', bookingId);

export const makeIssueReportDetailsUrl = (issueReportId: string) =>
    paths.issueReportDetails.replace(':issueReportId', issueReportId);
export const makeRentalIssueReportDetailsUrl = (issueReportId: string) =>
    paths.rentalViewIssueReports.replace(':issueReportId', issueReportId);

export const makeAddIssueReportForOrderUrl = (bookingId: string) =>
    paths.addIssueReportForBooking.replace(':bookingId', bookingId);

/**
 * The problem-report list narrowed to one booking. Both identifiers travel so
 * the list can apply the same match rule the booking page did — the order id
 * pins the individual sibling, the booking number catches reports that were
 * never linked to an order.
 */
export const ISSUE_REPORTS_BOOKING_PARAMS = {
    orderId: 'orderId',
    bookingNumber: 'bookingNumber',
} as const;

export const makeIssueReportsForBookingUrl = (booking: {
    orderId?: string | null;
    bookingNumber?: string | null;
}) => {
    const params = new URLSearchParams();
    if (booking.orderId) {
        params.set(ISSUE_REPORTS_BOOKING_PARAMS.orderId, booking.orderId);
    }
    if (booking.bookingNumber) {
        params.set(
            ISSUE_REPORTS_BOOKING_PARAMS.bookingNumber,
            booking.bookingNumber,
        );
    }
    const query = params.toString();
    return query ? `${paths.issueReports}?${query}` : paths.issueReports;
};

export const makeEditVenueBookingUrl = (bookingId: string) =>
    paths.editVenueBooking.replace(':bookingId', bookingId);

export const makeSeriesBookingDetailsUrl = (seriesId: string) =>
    paths.seriesBookingDetails.replace(':seriesId', seriesId);

export const makeProductDetailsUrl = (productId: string) =>
    paths.productDetails.replace(':productId', productId);

/**
 * Deep link into the Services product form for one product.
 *
 * Contract owned by the Services area, settled with that session 2026-08-23
 * (`docs/plans/service-configuration-area.md`, boundary block): a query param
 * — their page is tabbed, not per-product routed — plus the anchor whose tab
 * should open (`TAB_BY_ANCHOR` in `pages/services/ServicesPage.tsx`).
 *
 * They implement the `?product=` handling, and it arrives **with** their
 * product form (the post-W1.2a re-source of the pricing tab), not before.
 * Until then the link opens the right tab and ignores `product` — that
 * degradation is the agreed interim state, not an oversight.
 *
 * **The id is a group id today** (product ≡ `Group`; no Product table exists
 * in any schema yet — see `pages/products/productFleet.ts`). Part of the
 * contract: their handler accepts a group id now and a product id after
 * W1.2a, and is not keyed on a Product table that does not exist.
 *
 * Only `pricing` and `deposits` are product-scoped anchors. `pricing` is
 * stable. **`deposits` is provisional** — whether the deposit list stays in
 * Services or moves to the asset with W1.2 is open on their side; if it
 * moves, they retire the anchor with notice. `service-configuration` and
 * `payment-settings` also exist but are tenant-level, not product-scoped —
 * do not link to them from here.
 */
export const makeServicesProductUrl = (productId: string, anchor: 'pricing' | 'deposits' = 'pricing') =>
    `${paths.rentalProducts}?product=${encodeURIComponent(productId)}#${anchor}`;

export const makeAssetDetailsUrl = (assetId: string) =>
    paths.assetDetails.replace(':assetId', assetId);

export const makeStationDetailsUrl = (stationId: string) =>
    paths.stationDetails.replace(':stationId', stationId);

export const makeOrganizationDetailUrl = (organizationId: string) =>
    paths.customerOrganizationDetail.replace(':organizationId', organizationId);

export const makeCustomerAccountDetailUrl = (accountId: string) =>
    paths.customerAccountDetail.replace(':accountId', accountId);

/**
 * The bookings overview, pre-filtered. `BookingsPage` seeds its search box
 * from `?search=`, so this is how another screen hands it a filter — used by
 * the account detail view to show one customer's bookings.
 */
export const makeBookingsSearchUrl = (search: string) =>
    `${paths.bookings}?search=${encodeURIComponent(search)}`;

/**
 * The bookings overview, pre-filtered to one Fach. Deliberately the same
 * `?door=` param name the depot view uses for the opposite direction
 * (#2114's booking→locker link), so both directions read alike.
 */
export const makeBookingsForDoorUrl = (door: number) =>
    `${paths.bookings}?door=${encodeURIComponent(String(door))}`;

/**
 * The bookings overview, narrowed to room reservations still awaiting
 * approval (M8, #1809) — the target of the dashboard's "Awaiting approval"
 * figure. `BookingsPage` reads `?approval=awaiting` into its filter once.
 */
export const makeBookingsAwaitingApprovalUrl = () =>
    `${paths.rentalBooking}?${APPROVAL_QUERY_PARAM}=${AWAITING_APPROVAL_QUERY_VALUE}`;

/**
 * The bookings overview, narrowed to the bookings starting or ending today or
 * tomorrow (#3016) — the target of the dashboard's "Today and tomorrow"
 * figures. `awaitingApproval` adds the approval filter for the "Awaiting
 * approval" figure. `BookingsPage` reads `?day=` / `?dayEdge=` once.
 */
export const makeBookingsOnDayUrl = (
    listPath: string,
    filter: DayFilter,
    awaitingApproval = false,
) => {
    const approval = awaitingApproval ? `&${APPROVAL_QUERY_PARAM}=${AWAITING_APPROVAL_QUERY_VALUE}` : '';
    return `${listPath}?${dayFilterQuery(filter)}${approval}`;
};
